import React from "react";
import { IMat4, Mat4 } from "@base/math/Mat4";
import { MathUtil } from "@base/math/MathUtil";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { gtc } from "@base/util/Comma";
import { Auto, ClothType } from "@biz/definitions/SunSailDefinitions";
import {
    CurtainClothAlign, CurtainClothShape, CurtainConfectionBottomType, CurtainConfectionType, CurtainCornerArticleSlots, CurtainSuspensionType, CurtainTieBackType, CurtainType, DEFAULT_MAX_FIX_DIST, ICurtainClothAuto, ICurtainData,
    ICurtainTrackCorner, ICurtainTrackCornerRef, TrackJointType
} from "@biz/items/curtain/CurtainDefinitions";
import { CurtainClothName, CurtainPointName } from "@biz/items/curtain/CurtainNamedComponents";
import { ILocaContext } from "@biz/language/LocaContext";
import { IClothData } from "@biz/logic/ClothData";
import { getChecked } from "@biz/logic/getChecked";
import { Measure } from "@biz/logic/Measure";

export const TRACK_ARC_WIDTH = 0.4;
export const MAX_SLIDE_LENGTH = 6.0;
const SLIDE_WIDTH = 0.026;
export const DEFAULT_DOUBLE_MITTER_LENGTH = 0.2;
export const CURTAIN_FIX_FIRST = 0.02;

interface ITrackCornerInfos {
  dists: number[];
  dirs: IVec3[];
  normals: IVec3[];
}

export class CurtainLogic {
  static getWidthTexts(width: number, additionFactor: number): string {
    const final = CurtainLogic.calcFinalClothWidth(width, additionFactor);
    return `${Measure.toMM(final)} (${Measure.toMM(width)} + ${additionFactor * 100}%)`
  }

  static getHeightTexts(cloth: ICalculatedClothInfo): string {
    if (CurtainLogic.isSlopingShape(cloth.shape))
      return [cloth.height, cloth.height2].map((v) => Measure.toMM(v)).join(" / ")
    return Measure.toMM(cloth.height)
  }

  public static getClothName(index: number, _lc: ILocaContext): string | React.ReactElement {
    return React.createElement(CurtainClothName, { index })
  }

  public static getPointName(cornerIndex: number, _lc: ILocaContext): string | React.ReactElement {
    return React.createElement(CurtainPointName, { cornerIndex })
  }

  public static getTrackBoxMatrices(curtain: ICurtainData, trackIndex: number): IMat4[] {
    const s = 0.2;
    const matrices: IMat4[] = [];

    const track = curtain.tracks[trackIndex];

    let current = CurtainLogic.createTrackMatrix(curtain, trackIndex);
    for (let i = 0; i < track.corners.length - 1; i++) {
      const angle = CurtainLogic.getAngle(curtain, trackIndex, i);
      current = Mat4.muls(current, Mat4.createRotationY(angle));

      const len = curtain.tracks[trackIndex].corners[i + 1].length;
      matrices.push(Mat4.muls(current, Mat4.createTranslation(len * 0.5, 0, 0), Mat4.createScale(len, s, s)));
      const nextCorner = track.corners[i + 1];
      current = Mat4.muls(current, Mat4.createTranslation(nextCorner.length, 0, 0))
    }

    return matrices;
  }

  static createTrackMatrix(curtain: ICurtainData, trackIndex: number): IMat4 {
    const track = curtain.tracks[trackIndex];
    return Mat4.createTranslation(Vec3.add(track.pos, curtain.pos));
    // const rotateY = curtain.angle || 0;
    // let current = Mat4.createTranslation(Vec3.add(Vec3.rotateY(track.pos, rotateY), curtain.pos));
  }

  public static getTrackPointMatrices(curtain: ICurtainData, trackIndex: number): IMat4[] {
    const matrices: IMat4[] = [];

    const track = curtain.tracks[trackIndex];

    let current = CurtainLogic.createTrackMatrix(curtain, trackIndex);
    for (let i = 0; i < track.corners.length - 1; i++) {
      const angle = CurtainLogic.getAngle(curtain, trackIndex, i);
      current = Mat4.muls(current, Mat4.createRotationY(angle));
      matrices.push(current);
      const nextCorner = track.corners[i + 1];
      current = Mat4.muls(current, Mat4.createTranslation(nextCorner.length, 0, 0))
    }
    matrices.push(current)

    return matrices;
  }

  public static getTrackPositions(curtain: ICurtainData, trackIndex: number): IVec3[] {
    return CurtainLogic.getTrackPointMatrices(curtain, trackIndex).map((m) => Mat4.getTranslation(m))
  }

  public static getCornerPosition(curtain: ICurtainData, trackIndex: number, cornerIndex: number): IVec3 {
    const positions = CurtainLogic.getTrackPositions(curtain, trackIndex)
    return positions[cornerIndex];
  }

  public static getTrackPositionsForCloth(curtain: ICurtainData, trackIndex: number): IVec3[] {
    return CurtainLogic.getTrackPositions(curtain, trackIndex);
  }

  public static calcClothStartPoint(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    const startPoints = CurtainLogic.calcClothSegments(curtain, trackIndex);
    return startPoints[clothIndex].from;
  }

  public static getTrackLength(curtain: ICurtainData, trackIndex: number): number {
    const cornerCount = curtain.tracks[trackIndex].corners.length;
    let length = 0;
    for (let i = 1; i < cornerCount; i++) {
      length += curtain.tracks[trackIndex].corners[i].length;
    }
    return length;
  }

  public static calcClothSegments(curtain: ICurtainData, trackIndex: number): { from: number, to: number }[] {
    const groups: Record<string, number[]> = {};
    const result: { from: number, to: number }[] = [];

    const clothCount = curtain.tracks[trackIndex].clothes.length;
    for (let i = 0; i < clothCount; i++) {
      const align = CurtainLogic.getClothAlign(curtain, trackIndex, i);
      const anchorPoint = CurtainLogic.getClothAnchorPoint(curtain, trackIndex, i);
      const key = align + "_" + anchorPoint;
      if (!groups[key])
        groups[key] = [];
      groups[key].push(i);
    }

    const cornerStartPoints: number[] = [];
    const cornerCount = curtain.tracks[trackIndex].corners.length;
    {
      let startPos = 0;
      cornerStartPoints[0] = startPos;
      for (let i = 1; i < cornerCount; i++) {
        startPos += curtain.tracks[trackIndex].corners[i].length;
        cornerStartPoints[i] = startPos;
      }
    }

    const keys = Object.keys(groups);
    for (const key of keys) {
      const group = groups[key];
      if (group.length > 0) {
        const firstIndex = group[0];
        const anchorPoint = CurtainLogic.getClothAnchorPoint(curtain, trackIndex, firstIndex);
        const align = CurtainLogic.getClothAlign(curtain, trackIndex, firstIndex);
        const startPos = { left: cornerStartPoints[anchorPoint], center: (cornerStartPoints[anchorPoint] + cornerStartPoints[anchorPoint + 1]) * 0.5, right: cornerStartPoints[anchorPoint + 1] }[align];
        let len = 0;
        for (const gi of group) {
          const offset = CurtainLogic.getClothOffset(curtain, trackIndex, gi);
          len += offset;

          const width = CurtainLogic.getClothWidth(curtain, trackIndex, gi);
          len += width;
        }

        let x = 0;
        for (let i = 0; i < group.length; i++) {
          const gi = group[align === "right" ? group.length - i - 1 : i];
          const offset = CurtainLogic.getClothOffset(curtain, trackIndex, gi);
          const width = CurtainLogic.getClothWidth(curtain, trackIndex, gi);

          if (align === "left") {
            x += offset;
            const from = startPos + x;
            result[gi] = { from, to: from + width };
            x += width;
          } else if (align === "right") {
            x -= offset;
            x -= width;
            const from = startPos + x;
            result[gi] = { from, to: from + width };
          } else if (align === "center") {
            x += offset;
            const from = startPos - len * 0.5 + x;
            result[gi] = { from, to: from + width };
            x += width;
          }
        }
      }
    }
    return result;
  }

  static getCornerCount(curtain: ICurtainData, trackIndex: number): number {
    return curtain.tracks[trackIndex].corners.length;
  }

  static getJointType(curtain: ICurtainData, trackIndex: number, cornerIndex: number): TrackJointType {
    if (curtain.curtainType === "rope")
      return "stop";
    const track = curtain.tracks[trackIndex];
    if (cornerIndex === 0 || cornerIndex >= track.corners.length - 1)
      return "stop";
    const jointType = track.corners[cornerIndex].jointType;
    return jointType;
  }

  static getCornerAngle(curtain: ICurtainData, trackIndex: number, cornerIndex: number): number {
    const track = curtain.tracks[trackIndex];
    const corner = track.corners[cornerIndex];
    if (CurtainLogic.getJointType(curtain, trackIndex, cornerIndex) === "arc") {
      return corner.angle >= 0 ? Math.PI * 0.5 : -Math.PI * 0.5;
    }
    return corner.angle;
  }

  static getCurtainRotation(curtain: ICurtainData): number {
    return curtain.rotationY || 0;
  }

  static getAngle(curtain: ICurtainData, trackIndex: number, cornerIndex: number): number {
    if (cornerIndex === 0)
      return curtain.tracks[trackIndex].angle + CurtainLogic.getCurtainRotation(curtain);
    return CurtainLogic.getCornerAngle(curtain, trackIndex, cornerIndex);
  }

  static getDefaultAdditionFactor(clothType: ClothType | undefined): number {
    switch (clothType) {
      case ClothType.Austrosail_Screen20: return 0.2;
      case ClothType.Austrosail_Screen22: return 0.2;
      case ClothType.Austronet932: return 0.1;
      case ClothType.Austronet950: return 0.1;
      case ClothType.Austronet960: return 0.1;
      case ClothType.Silvanet: return 0.2;
      case ClothType.ChillSail: return 0.6;
      case ClothType.Finoo: return 0.1;
      case ClothType.FinooPro: return 0.1;
    }
    return 0;
  }

  static calcClothAdditionFactor(inputAdditionFactor: Auto<number> | undefined, clothType: ClothType | undefined): number {
    if (inputAdditionFactor === undefined || inputAdditionFactor === "auto")
      return CurtainLogic.getDefaultAdditionFactor(clothType);
    return inputAdditionFactor;
  }

  static isSlopingShape(shape: CurtainClothShape): boolean {
    return shape === "slopingLeft" || shape === "slopingRight";
  }

  static getClothWidth(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return cloth.width !== undefined ? cloth.width : curtain.clothGlobal.width;
  }

  static getClothWidth2(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    return CurtainLogic.getClothWidth(curtain, trackIndex, clothIndex);
  }

  static calcFinalClothWidth(width: number, materialAdditionFactor: number): number {
    const finalClothWidth = width * (1 + materialAdditionFactor);
    return finalClothWidth;
  }

  static getFinalClothWidthMid(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    const clothWidth = CurtainLogic.getClothWidth(curtain, trackIndex, clothIndex);
    const additionFactor = CurtainLogic.getClothAdditionFactor(curtain, trackIndex, clothIndex);
    return CurtainLogic.calcFinalClothWidth(clothWidth, additionFactor);
  }

  static getFinalClothWidthTop(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    return CurtainLogic.getFinalClothWidthMid(curtain, trackIndex, clothIndex);
  }

  static getClothShape(_curtain: ICurtainData, _trackIndex: number, _clothIndex: number): CurtainClothShape {
    return "rect"
  }

  static getClothArea(curtain: ICurtainData, iTrack: number, iCloth: number): number {
    const finalClothWidthMid = CurtainLogic.getFinalClothWidthMid(curtain, iTrack, iCloth);
    const clothHeight = CurtainLogic.getClothHeight(curtain, iTrack, iCloth);
    const clothHeight2 = CurtainLogic.getClothHeight2(curtain, iTrack, iCloth);
    return CurtainLogic.calcClothArea(finalClothWidthMid, clothHeight, clothHeight2);
  }

  static calcClothArea(finalClothWidth: number, clothHeight1: number, clothHeight2: number): number {
    const area = finalClothWidth * (clothHeight1 + clothHeight2) * 0.5;
    const sunSailArea = MathUtil.round(area, 2);
    return sunSailArea;
  }

  static getClothHeight(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return cloth.height !== undefined ? cloth.height : curtain.clothGlobal.height;
  }

  static getClothHeight2(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    return CurtainLogic.getClothHeight(curtain, trackIndex, clothIndex);
  }

  static getClothAdditionFactor(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    const clothAdditionFactor = clothIndex === undefined ? undefined : curtain.tracks[trackIndex].clothes[clothIndex]?.additionFactor;
    const inputAdditionFactor = clothAdditionFactor !== undefined ? clothAdditionFactor : curtain.clothGlobal.additionFactor;
    return CurtainLogic.calcClothAdditionFactor(inputAdditionFactor, CurtainLogic.getCloth(curtain, trackIndex, clothIndex)?.clothType)
  }

  static getClothType(curtain: ICurtainData, trackIndex: number, clothIndex: number): ClothType | undefined {
    const cloth = CurtainLogic.getCloth(curtain, trackIndex, clothIndex);
    return cloth?.clothType;
  }

  static getClothGroup(curtain: ICurtainData, trackIndex: number, clothIndex: number): string {
    const cloth = CurtainLogic.getCloth(curtain, trackIndex, clothIndex);
    return cloth?.articleId?.group || "";
  }

  static getCloth(curtain: ICurtainData, trackIndex: number, clothIndex: number): IClothData | undefined {
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return cloth.cloth !== undefined ? cloth.cloth : curtain.clothGlobal.cloth;
  }

  static getClothColorId(curtain: ICurtainData, trackIndex: number, clothIndex: number): string {
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return (cloth.cloth !== undefined ? cloth.colorId : curtain.clothGlobal.colorId) || "";
  }

  static getClothConfectionType(curtain: ICurtainData, trackIndex?: number, clothIndex?: number): CurtainConfectionType {
    if (trackIndex === undefined || clothIndex === undefined)
      return curtain.clothGlobal.confectionType;
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return cloth.confectionType !== undefined ? cloth.confectionType : curtain.clothGlobal.confectionType;
  }

  static getClothConfectionBottomType(curtain: ICurtainData, trackIndex?: number, clothIndex?: number): CurtainConfectionBottomType {
    if (trackIndex === undefined || clothIndex === undefined)
      return curtain.clothGlobal.confectionBottomType;
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return cloth.confectionBottomType !== undefined ? cloth.confectionBottomType : curtain.clothGlobal.confectionBottomType;
  }

  static getClothAlign(curtain: ICurtainData, trackIndex: number, clothIndex: number): CurtainClothAlign {
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return cloth.align || "left";
  }

  static getClothAnchorPoint(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return Math.min(cloth.anchorPoint || 0, curtain.tracks[trackIndex].corners.length - 2);
  }

  static getClothOffset(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return cloth.offset || 0;
  }

  static #getClothSuspensionType(curtain: ICurtainData, trackIndex?: number, clothIndex?: number): CurtainSuspensionType {
    if (trackIndex === undefined || clothIndex === undefined)
      return curtain.clothGlobal.suspensionType;
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return (cloth.suspensionType !== undefined ? cloth.suspensionType : curtain.clothGlobal.suspensionType);
  }

  public static getClothSuspensionType(curtain: ICurtainData, trackIndex?: number, clothIndex?: number): CurtainSuspensionType {
    return getChecked({
      selection: CurtainLogic.#getClothSuspensionType(curtain, trackIndex, clothIndex),
      validItems: CurtainLogic.getPossibleSuspensionTypes(curtain.curtainType, CurtainLogic.getClothConfectionType(curtain, trackIndex, clothIndex) || "flap"),
      emptySelection: "__selectionRequired__"
    }) || "none"
  }

  static getTieBackType(curtain: ICurtainData, trackIndex: number, clothIndex: number): CurtainTieBackType | undefined {
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return cloth.tieBackType !== undefined ? cloth.tieBackType : curtain.clothGlobal.tieBackType;
  }

  static isTieBackLengthAuto(curtain: ICurtainData, trackIndex: number, clothIndex: number): boolean {
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    return cloth.tieBackLength === "auto";
  }

  static calcRecommendTieBackLength(clothGroup: string, finalClothWidthMid: number): number {
    const isScreen = clothGroup === "920" || clothGroup === "922";

    const factors = isScreen ? [50, 10, 160] : [50, 20, 165];
    const c = [factors[0], finalClothWidthMid * factors[1], Math.sqrt(finalClothWidthMid) * factors[2]]
    const result = Math.max(10, (c[0] + c[1] + c[2]))
    const rounded = Math.ceil(result / 5) * 5;
    return rounded / 1000;
  }

  static getTieBackLength(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    const cloth = curtain.tracks[trackIndex].clothes[clothIndex];
    const ret = cloth.tieBackLength === "auto" ? CurtainLogic.calcRecommendTieBackLength(CurtainLogic.getClothGroup(curtain, trackIndex, clothIndex), CurtainLogic.getFinalClothWidthMid(curtain, trackIndex, clothIndex)) : cloth.tieBackLength;
    return MathUtil.round(ret, 2);
  }

  static calcSimpleCorners(curtain: ICurtainData, trackIndex: number): ICurtainTrackCornerRef[] {

    const track = curtain.tracks[trackIndex];
    const _corners = track.corners;

    const simpleCorners: ICurtainTrackCornerRef[] = [];
    for (let i = 0; i < _corners.length; i++) {
      const c = _corners[i];

      let lastLengthOffset = 0;
      if (i > 0) {
        if (CurtainLogic.isDoubleMitter(curtain, trackIndex, i - 1)) {
          const angle = CurtainLogic.getAngle(curtain, trackIndex, i - 1);
          lastLengthOffset = -CurtainLogic.calcDoubleMitterDist(Math.abs(angle), DEFAULT_DOUBLE_MITTER_LENGTH)
        }
      }

      const jointType = CurtainLogic.getJointType(curtain, trackIndex, i);
      const angle = CurtainLogic.getAngle(curtain, trackIndex, i);
      const length = c.length;
      if (CurtainLogic.isDoubleMitter(curtain, trackIndex, i)) {
        const doubleMitterDist = CurtainLogic.calcDoubleMitterDist(Math.abs(angle), DEFAULT_DOUBLE_MITTER_LENGTH);
        const newCorner: ICurtainTrackCorner = {
          jointType,
          angle: angle * 0.5,
          length: length - doubleMitterDist + lastLengthOffset,
        }
        simpleCorners.push({ corner: newCorner, index: i });
        const newCorner2: ICurtainTrackCorner = {
          jointType: "miter",
          angle: angle * 0.5,
          length: DEFAULT_DOUBLE_MITTER_LENGTH,
        }
        simpleCorners.push({ corner: newCorner2, index: i });
        lastLengthOffset = -doubleMitterDist;
      } else {
        simpleCorners.push({
          corner: {
            jointType,
            angle,
            length: length + lastLengthOffset,
          },
          index: i
        });
        lastLengthOffset = 0;
      }
    }

    return simpleCorners;
  }

  static calcClothInfos(curtain: ICurtainData): ICalculatedClothInfo[] {
    const clothInfos: ICalculatedClothInfo[] = [];
    for (let iTrack = 0; iTrack < curtain.tracks.length; iTrack++) {
      for (let iCloth = 0; iCloth < curtain.tracks[iTrack].clothes.length; iCloth++) {
        const finalClothWidthMid = CurtainLogic.getFinalClothWidthMid(curtain, iTrack, iCloth);
        const finalClothWidthTop = CurtainLogic.getFinalClothWidthTop(curtain, iTrack, iCloth);
        const maxFixDist = CurtainLogic.calcClothMaxFixDist(curtain, iTrack, iCloth);
        const fixCount = CurtainLogic._calcClothFixCount(finalClothWidthTop, maxFixDist);
        const fixDist = CurtainLogic._calcClothFixDist(finalClothWidthTop, CurtainLogic._calcClothFixCount(finalClothWidthTop, maxFixDist))

        const tieBackType = CurtainLogic.getTieBackType(curtain, iTrack, iCloth);
        const ci: ICalculatedClothInfo = {
          shape: curtain.tracks[iTrack].clothes[iCloth].shape || "rect",
          count: 1, // cloth.count
          trackIndex: iTrack,
          clothIndex: iCloth,
          cloth: CurtainLogic.getCloth(curtain, iTrack, iCloth),
          colorId: CurtainLogic.getClothColorId(curtain, iTrack, iCloth),
          width: CurtainLogic.getClothWidth(curtain, iTrack, iCloth),
          width2: CurtainLogic.getClothWidth2(curtain, iTrack, iCloth),
          height: CurtainLogic.getClothHeight(curtain, iTrack, iCloth),
          height2: CurtainLogic.getClothHeight2(curtain, iTrack, iCloth),
          finalClothWidthMid,
          finalClothWidthTop,
          additionFactor: CurtainLogic.getClothAdditionFactor(curtain, iTrack, iCloth),
          confectionType: CurtainLogic.getClothConfectionType(curtain, iTrack, iCloth),
          confectionBottomType: CurtainLogic.getClothConfectionBottomType(curtain, iTrack, iCloth),
          suspensionType: CurtainLogic.getClothSuspensionType(curtain, iTrack, iCloth),
          maxFixDist: CurtainLogic.calcClothMaxFixDist(curtain, iTrack, iCloth),
          tieBackType,
          tieBackLength: CurtainLogic.getTieBackLength(curtain, iTrack, iCloth),
          tieBackCount: tieBackType ? 1 : 0,
          fixPositions: CurtainLogic._calcClothFixPositions(fixCount, fixDist)
        }
        clothInfos.push(ci)
      }
    }
    return clothInfos;
  }

  static calcGuides(curtain: ICurtainData): ICalculatedGuideInfo[] {
    let guides: ICalculatedGuideInfo[] = [];
    for (let iTrack = 0; iTrack < curtain.tracks.length; iTrack++) {
      const trackGuides = CurtainLogic.calcTrackGuides(curtain, iTrack)
      guides = guides.concat(trackGuides);
    }
    return guides;
  }

  static calcTrackGuides(curtain: ICurtainData, trackIndex: number): ICalculatedGuideInfo[] {
    const simpleCorners: ICurtainTrackCornerRef[] = CurtainLogic.calcSimpleCorners(curtain, trackIndex);

    const guides: ICalculatedGuideInfo[] = [];
    for (let i = 1; i < simpleCorners.length; i++) {

      const prev = simpleCorners[i - 1];
      const current = simpleCorners[i];

      const prevCorner = getJointTypeSlideOffsetAngle(prev.corner.jointType, prev.corner.angle);
      const currentCorner = getJointTypeSlideOffsetAngle(current.corner.jointType, current.corner.angle);

      const guide: ICalculatedGuideInfo = {
        type: "slide",
        count: 1,
        articleNumber: "00E13001129",
        length: current.corner.length + currentCorner.offset + prevCorner.offset,
        angleLeft: prevCorner.angle,
        angleRight: -currentCorner.angle,
      }
      if (prev.index !== current.index) {
        guide.notchStart = CurtainLogic.getCornerArticle(curtain, trackIndex, prev.index, "startNotch");
        guide.notchEnd = CurtainLogic.getCornerArticle(curtain, trackIndex, prev.index + 1, "endNotch");
      }

      if (guide.length > 0)
        guides.push(guide);

      if (current.corner.jointType === "arc") {
        const guide: IArcGuideInfo = {
          type: "arc",
          count: 1,
          angle: currentCorner.angle,
          articleNumber: "00E13000758"
        }
        guides.push(guide);
      } else {
        if (current.corner.jointType === "miter" && CurtainLogic.isDoubleMitterAngle(current.corner.angle)) {
          guides.push({
            type: "slide",
            count: 1,
            articleNumber: "00E13001129",
            length: DEFAULT_DOUBLE_MITTER_LENGTH,
            angleLeft: current.corner.angle * 0.5,
            angleRight: -current.corner.angle * 0.5,
          })
        }
      }
    }

    CurtainLogic.fixMaxGuideLength(guides);

    return guides;
  }

  static fixMaxGuideLength(guides: ICalculatedGuideInfo[]): void {
    let guideIndex = 0;
    while (guideIndex < guides.length) {
      const current = guides[guideIndex];
      if (current.type === "arc") {
        guideIndex++;
      } else {
        if (current.length <= MAX_SLIDE_LENGTH)
          guideIndex++;
        else {
          const newGuide: ISlideGuideInfo = {
            type: "slide",
            count: current.count,
            articleNumber: current.articleNumber,
            length: current.length - MAX_SLIDE_LENGTH,
            angleLeft: 0,
            angleRight: current.angleRight,
            notchEnd: current.notchEnd
          }
          current.length = MAX_SLIDE_LENGTH;
          current.angleRight = 0;
          current.notchEnd = undefined;
          guides.splice(guideIndex + 1, 0, newGuide);
          guideIndex++
        }
      }
    }
  }

  static isMitterAngle(angle: number): boolean {
    const deg = MathUtil.rad2deg(angle);
    return gtc(Math.abs(deg), 0, 1);
  }

  static isDoubleMitterAngle(angle: number): boolean {
    return Math.abs(angle) > Math.PI / 4;
  }

  static isDoubleMitter(curtain: ICurtainData, trackIndex: number, cornerIndex: number): boolean {
    return CurtainLogic.getJointType(curtain, trackIndex, cornerIndex) === "miter" && CurtainLogic.isDoubleMitterAngle(CurtainLogic.getAngle(curtain, trackIndex, cornerIndex));
  }

  static calcMitter(angle: number): number {
    return SLIDE_WIDTH * 0.5 * Math.tan(angle * 0.5)
  }

  static calcDoubleMitterDist(angle: number, l: number = DEFAULT_DOUBLE_MITTER_LENGTH): number {
    return (l * 0.5) / Math.sin(Math.PI * 0.5 - angle * 0.5);
  }

  static _calcClothFixCount(finalClothWidthTop: number, maxFixDist: number): number {
    const _fixDist = maxFixDist; // 0.12-0.25
    const fixCount = 1 + Math.ceil((finalClothWidthTop - CURTAIN_FIX_FIRST * 2) / _fixDist);
    return fixCount;
  }

  static _calcClothFixDist(finalClothWidthTop: number, fixCount: number): number {
    const dist = (finalClothWidthTop - CURTAIN_FIX_FIRST * 2) / (fixCount - 1);
    return dist;
  }

  static calcClothMaxFixDist(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    return curtain.tracks[trackIndex].clothes[clothIndex].maxFixDist || curtain.clothGlobal.maxFixDist || DEFAULT_MAX_FIX_DIST;
  }

  static calcClothFixCount(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    const finalWidth = CurtainLogic.getFinalClothWidthTop(curtain, trackIndex, clothIndex);
    const maxFixDist = CurtainLogic.calcClothMaxFixDist(curtain, trackIndex, clothIndex);
    return CurtainLogic._calcClothFixCount(finalWidth, maxFixDist);
  }

  static calcClothFixDist(curtain: ICurtainData, trackIndex: number, clothIndex: number): number {
    const finalWidth = CurtainLogic.getFinalClothWidthTop(curtain, trackIndex, clothIndex);
    const fixCount = CurtainLogic.calcClothFixCount(curtain, trackIndex, clothIndex);
    return CurtainLogic._calcClothFixDist(finalWidth, fixCount);
  }

  static _calcClothFixPositions(fixCount: number, fixDist: number): number[] {
    const positions: number[] = [];
    let pos = CURTAIN_FIX_FIRST;
    positions.push(pos);
    for (let i = 1; i < fixCount; i++) {
      pos += fixDist;
      positions.push(pos);
    }
    return positions;
  }

  static calcMitterArticleCount(curtain: ICurtainData, trackIndex: number): number {
    let mitterCount = 0;
    const guides = CurtainLogic.calcTrackGuides(curtain, trackIndex)
    for (let i = 0; i < guides.length; i++) {
      const g = guides[i];
      if (g.type === "slide") {
        if (CurtainLogic.isMitterAngle(g.angleLeft))
          mitterCount++;
        if (CurtainLogic.isMitterAngle(g.angleRight))
          mitterCount++;
      }
    }
    return mitterCount;
  }

  static topToClothDist(confectionType: CurtainConfectionType, suspensionType: CurtainSuspensionType): number {
    // https://plaspack.atlassian.net/browse/APP22-629
    switch (suspensionType) {
      case "plasticRoll":
        return 0.02;
      case "metalRoll":
        return 0.02;
      case "glider":
        return 0.02;
      case "carabinerPulley":
        return 0.03;
      case "carabiner":
        return confectionType === "grommet" ? 0.016 : 0.01;
      case "none":
        return -0.015; // rope
    }
    return 0.0;
  }


  static getArticleNumberForSuspensionType(suspensionType: CurtainSuspensionType): string | undefined {
    return {
      carabiner: "00E13001224",
      carabinerPulley: "00E13001952",
      glider: "00E13001774",
      plasticRoll: "00E13001300",
      metalRoll: "00E13001715",
      "none": undefined,
    }[suspensionType];
  }

  static curtainInfos: Record<CurtainType, {
    possibleSuspensionTypes: Record<CurtainConfectionType, CurtainSuspensionType[]>
  }> = {
      rope: {
        possibleSuspensionTypes: {
          flap: ["carabiner", "carabinerPulley"],
          grommet: ["carabiner", "carabinerPulley", "none"],
          pe: ["none"],
          special: ["none"]
        }
      },
      guide: {
        possibleSuspensionTypes: {
          flap: ["glider", "plasticRoll", "metalRoll"],
          grommet: ["plasticRoll", "metalRoll"],
          pe: ["none"],
          special: ["none"]
        }
      },
      clothOnly: {
        possibleSuspensionTypes: {
          flap: ["none", "glider", "plasticRoll", "metalRoll", "carabiner", "carabinerPulley"],
          grommet: ["none", "carabiner", "carabinerPulley", "plasticRoll", "metalRoll"],
          pe: ["none"],
          special: ["none"]
        }
      }
    };
  static getPossibleSuspensionTypes(curtainType: CurtainType, confectionType: CurtainConfectionType): CurtainSuspensionType[] {
    return CurtainLogic.curtainInfos[curtainType]?.possibleSuspensionTypes[confectionType] || [];
  }

  static calcTotalNetArea(data: ICurtainData): number {
    let total = 0;
    for (let iTrack = 0; iTrack < data.tracks.length; iTrack++) {
      for (let iCloth = 0; iCloth < data.tracks[iTrack].clothes.length; iCloth++) {
        const area = CurtainLogic.getClothArea(data, iTrack, iCloth) * 1;
        total += area;
      }
    }
    return total;
  }

  static getMaxTrackPartLength(curtainType: CurtainType): number | undefined {
    if (curtainType === "rope")
      return 4.0;
    return undefined;
  }

  static getCornerArticle(data: ICurtainData, trackIndex: number, cornerIndex: number, slot: CurtainCornerArticleSlots): string {
    const track = data.tracks[trackIndex];
    if (slot.startsWith("end") && cornerIndex === 0)
      return "";
    if (slot.startsWith("start") && cornerIndex >= track.corners.length - 1)
      return "";

    const articleId = track.corners[cornerIndex].articleSlots?.[slot];
    if (articleId === "__none__")
      return "";
    return articleId || "";
  }

  static isRopeBorder(curtain: ICurtainData, trackIndex: number, cornerIndex: number): boolean {
    if (cornerIndex === 0)
      return true;
    const track = curtain.tracks[trackIndex];
    if (cornerIndex === track.corners.length - 1)
      return true;
    const startTerminal = CurtainLogic.getCornerArticle(curtain, trackIndex, cornerIndex, "startTerminal");
    const endTerminal = CurtainLogic.getCornerArticle(curtain, trackIndex, cornerIndex, "endTerminal");
    return !!(startTerminal || endTerminal);
  }

  static getRopeParts(curtain: ICurtainData, trackIndex: number): ICalculatedRopeInfo[] {
    const ropes: ICalculatedRopeInfo[] = [];
    const track = curtain.tracks[trackIndex];
    let ropeLength = 0;
    for (let iCorner = 1; iCorner < track.corners.length; iCorner++) {
      const rl = track.corners[iCorner].length;
      ropeLength += rl;
      const startTerminal = CurtainLogic.getCornerArticle(curtain, trackIndex, iCorner, "startTerminal");
      const endTerminal = CurtainLogic.getCornerArticle(curtain, trackIndex, iCorner, "endTerminal");
      if ((startTerminal || endTerminal) && ropeLength > 0) {
        ropes.push({
          length: ropeLength,
          count: 1,
          startTerminal: CurtainLogic.getCornerArticle(curtain, trackIndex, iCorner - 1, "startTerminal"),
          endTerminal: CurtainLogic.getCornerArticle(curtain, trackIndex, iCorner, "endTerminal"),
        });
        ropeLength = 0;
      }
    }
    if (ropeLength > 0)
      ropes.push({
        length: ropeLength,
        count: 1,
        startTerminal: CurtainLogic.getCornerArticle(curtain, trackIndex, 0, "startTerminal"),
        endTerminal: CurtainLogic.getCornerArticle(curtain, trackIndex, track.corners.length - 1, "endTerminal"),
      });
    return ropes;
  }

  static calcClothCenter(curtain: ICurtainData, trackIndex: number, clothIndex: number): IVec3 {
    const startPos = CurtainLogic.calcClothStartPoint(curtain, trackIndex, clothIndex)
    const trackPositions = CurtainLogic.getTrackPositionsForCloth(curtain, trackIndex);
    const width = CurtainLogic.getClothWidth(curtain, trackIndex, clothIndex);
    const tci = CurtainLogic.calcTrackCornerInfos(trackPositions);
    const relativePos = CurtainLogic.calcRelativeTrackPosition(tci, startPos + width * 0.5);
    return Vec3.addScaled(trackPositions[relativePos.index], tci.dirs[relativePos.index], relativePos.dist);
  }

  static calcClothMeasureCorners(curtain: ICurtainData, trackIndex: number, clothIndex: number): IVec3[] {
    const trackPositions = CurtainLogic.getTrackPositionsForCloth(curtain, trackIndex);
    const startPos = CurtainLogic.calcClothStartPoint(curtain, trackIndex, clothIndex)
    const width = CurtainLogic.getClothWidth(curtain, trackIndex, clothIndex);
    const height = CurtainLogic.getClothHeight(curtain, trackIndex, clothIndex);

    const topToClothDist = CurtainLogic.topToClothDist(CurtainLogic.getClothConfectionType(curtain, trackIndex, clothIndex), CurtainLogic.getClothSuspensionType(curtain, trackIndex, clothIndex));
    const corners = CurtainLogic.calcClothMeasurePositions(trackPositions, startPos, width, height, topToClothDist);
    return corners;
  }

  static calcClothMeasurePositions(trackPositions: IVec3[], relativeStartPos: number, clothWidth: number, clothHeight: number | number[], topToClothDist: number): IVec3[] {
    const posOnTrack = CurtainLogic.calcClothMeasurePositionsOnTrack(trackPositions, relativeStartPos, clothWidth);
    const corners: IVec3[] = [];
    corners.push(Vec3.addElements(posOnTrack[0], 0, - topToClothDist, 0))
    for (let i = 0; i < posOnTrack.length; i++) {
      corners.push(Vec3.addElements(posOnTrack[i], 0, -clothHeight - topToClothDist, 0))
    }
    return corners;
  }

  static calcClothMeasurePositionsOnTrack(trackPositions: IVec3[], relativeStartPos: number, clothWidth: number): IVec3[] {
    const corners: IVec3[] = [];
    const tci = CurtainLogic.calcTrackCornerInfos(trackPositions);
    const relativePos = CurtainLogic.calcRelativeTrackPosition(tci, relativeStartPos);
    corners.push(Vec3.addScaled(trackPositions[relativePos.index], tci.dirs[relativePos.index], relativePos.dist));

    {
      let index = relativePos.index;
      let trackDist = Vec3.len(trackPositions[index], trackPositions[index + 1]) - relativePos.dist;
      let restWidth = clothWidth;
      while (restWidth > trackDist && index < trackPositions.length - 2) {
        index++;
        const currentPos = trackPositions[index];
        corners.push(currentPos)
        restWidth -= trackDist;
        trackDist = Vec3.len(trackPositions[index], trackPositions[index + 1]);
      }
      if (restWidth > 0) {
        const relativePos = CurtainLogic.calcRelativeTrackPosition(tci, relativeStartPos + clothWidth);
        const currentPos = Vec3.addScaled(trackPositions[relativePos.index], tci.dirs[relativePos.index], relativePos.dist)
        corners.push(currentPos)
      }
    }
    return corners;
  }

  static calcTrackCornerInfos(corners: IVec3[]): ITrackCornerInfos {
    const data: ITrackCornerInfos = {
      dists: [0],
      dirs: [],
      normals: []
    }
    let current = 0;
    for (let i = 0; i < corners.length - 1; i++) {
      current += Vec3.len(corners[i], corners[i + 1]);
      data.dists.push(current)
      const dir = Vec3.dir(corners[i], corners[i + 1]);
      data.dirs.push(dir)
      data.normals.push(Vec3.normalized(Vec3.from(-dir.z, 0, dir.x)))
    }
    return data;
  }

  static calcRelativeTrackPosition(tci: ITrackCornerInfos, x: number): {
    index: number;
    dist: number;
  } {
    let _index = 0;
    const dists = tci.dists;
    while (_index < dists.length - 1 && x > dists[_index]) {
      _index++;
    }
    const index = Math.max(_index - 1, 0);
    return {
      index,
      dist: x - dists[index]
    }
  }

  static calcTrackCornerPosFn(corners: IVec3[]): (x: number) => { p: IVec3; n: IVec3 } {
    const tci = CurtainLogic.calcTrackCornerInfos(corners);

    return (x) => {
      const { index, dist } = CurtainLogic.calcRelativeTrackPosition(tci, x);

      const dists = tci.dists;
      const minX = Math.min(Math.abs(dists[index + 1] - x), Math.abs(dist))

      return {
        p: Vec3.addScaled(corners[index], tci.dirs[index], dist),
        n: minX < 0.2 ? Vec3.mul(tci.normals[index], 0.0 + minX * 5) : tci.normals[index],
      }
    }
  }

  static curlGradeFunction(x: number, amplitude: number, halfCurls: number, width: number): number {
    const inner = halfCurls * Math.PI / (width - CURTAIN_FIX_FIRST * 2);
    return amplitude * Math.cos((x - CURTAIN_FIX_FIRST) * inner) * inner
  }

  static _calcClothMountMatrices(corners: IVec3[], startPos: number, width: number, amplitude: number, halfCurls: number, fn: (pos: IVec3, rotation: number) => IMat4): IMat4[] {
    const getTrackPos = CurtainLogic.calcTrackCornerPosFn(corners);

    const matrices: IMat4[] = [];
    for (let ic = 0; ic < halfCurls + 1; ic++) {
      const x = CURTAIN_FIX_FIRST + ((width - CURTAIN_FIX_FIRST * 2) / halfCurls) * ic;
      const { p, n } = getTrackPos(x + startPos);

      const grade = CurtainLogic.curlGradeFunction(x, amplitude, halfCurls, width)
      const r1 = Math.atan2(grade, 1);
      const rotation = Math.atan2(-n.z, n.x);
      const m = fn(p, rotation - r1);
      matrices.push(m);
    }
    return matrices;
  }

  static getPossibleConfectionTypes(curtainType: CurtainType): CurtainConfectionType[] {
    switch (curtainType) {
      case "clothOnly": {
        return ["flap", "grommet", "pe", "special"];
      }
    }
    return ["flap", "grommet"];
  }

  static hasTrack(curtain: ICurtainData, trackIndex: number): boolean {
    return curtain.tracks.length > trackIndex;
  }

  static hasCorner(curtain: ICurtainData, trackIndex: number, cornerIndex: number): boolean {
    return curtain.tracks.length > trackIndex && curtain.tracks[trackIndex].corners.length > cornerIndex;
  }

  static hasCloth(curtain: ICurtainData, trackIndex: number, clothIndex: number): boolean {
    return curtain.tracks.length > trackIndex && curtain.tracks[trackIndex].clothes.length > clothIndex;
  }
}

export interface ISlideGuideInfo {
  type: "slide"
  count: number;
  articleNumber: string;
  length: number;
  angleLeft: number;
  angleRight: number;
  notchStart?: string;
  notchEnd?: string;
}

export interface IArcGuideInfo {
  type: "arc",
  count: number;
  articleNumber: string;
  angle: number;
}

export type ICalculatedGuideInfo = ISlideGuideInfo | IArcGuideInfo;

export interface ICalculatedRopeInfo {
  count: number;
  length: number;
  startTerminal?: string;
  endTerminal?: string;

}

export interface ICalculatedClothInfo extends ICurtainClothAuto {
  count: number;
  finalClothWidthTop: number;
  finalClothWidthMid: number;
  trackIndex: number;
  clothIndex: number;
  tieBackType: CurtainTieBackType | undefined;
  tieBackLength: number;
  tieBackCount: number;
  fixPositions: number[];
  additionFactor: number;

  shape: CurtainClothShape;
  height2: number;
  width2: number;
}

function getJointTypeSlideOffsetAngle(jointType: TrackJointType, angle: number): { offset: number; angle: number } {
  switch (jointType) {
    case "arc":
      return {
        offset: -TRACK_ARC_WIDTH,
        angle: 0
      }
    case "miter": {
      const absAngle = Math.abs(angle);
      if (CurtainLogic.isDoubleMitterAngle(absAngle)) {
        throw new Error("Not supported")
        // const doubleMitterDist = CurtainLogic.calcDoubleMitterDist(absAngle, DEFAULT_DOUBLE_MITTER_LENGTH);
        // const offset = -doubleMitterDist + CurtainLogic.calcMitter(absAngle * 0.5);
        // return {
        //   offset,
        //   angle: angle * 0.25
        // }
      } else {
        return {
          offset: CurtainLogic.calcMitter(absAngle),
          angle: angle * 0.5
        }
      }
    }
  }
  return {
    offset: 0,
    angle: 0
  };
}